import { useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { isBrowser } from "react-device-detect";
import { useTheme, Skeleton } from "@mui/material";
import {
  snazzyDesktop,
  snazzyMobile,
  contactEyebrowString,
  contactBannerString,
  contactIntroString,
  visitUsString,
  street,
  city,
  state,
  zip,
  getDirectionsString,
  sundayWorshipString,
  morningEveningServicesString,
  viewFullScheduleString,
  serviceTimesPath,
} from "../../constants";
import { ADDRESS_MAPS_URL } from "../../menu/menuShared";
import { WEEKLY_SCHEDULE } from "../../helpers/upcomingServices";
import UrgentMessageDisplay from "../../components/UrgentMessageDisplay";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";

// Lazy load the components
const ContactForm = lazy(() => import("./contactForm/contactForm"));
const SentSuccess = lazy(() => import("./sentSuccess"));

const snazzy = () => {
  return isBrowser ? snazzyDesktop : snazzyMobile;
};

const SUNDAY_SERVICES = WEEKLY_SCHEDULE.filter((service) => service.dayOfWeek === 0);

const Contact = () => {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);
  const [isSent, setIsSent] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  const handleSent = () => {
    setIsSent(true);
  };

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
  };

  const headingStyle = {
    fontFamily: theme.typography.secondaryFontFamily,
    fontWeight: 600,
    color: theme.palette.primary.dark,
    margin: "0 0 12px",
  };

  const bodyStyle = {
    fontFamily: theme.typography.paragraphFontFamily,
    fontSize: "15px",
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
  };

  const primaryLinkStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: "14px",
    color: theme.palette.primary.main,
    textDecoration: "none",
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.brand.linen,
        fontFamily: theme.typography.fontFamily,
        paddingTop: "var(--nav-height)",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <UrgentMessageDisplay page="Contact" />

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "36px 24px 8px" }}>
        <div style={eyebrowStyle}>{contactEyebrowString(language)}</div>
        <h1 style={{ ...headingStyle, fontSize: "min(9vw, 40px)" }}>
          {contactBannerString(language)}
        </h1>
        <p style={{ ...bodyStyle, margin: 0, maxWidth: "560px" }}>
          {contactIntroString(language)}
        </p>
      </div>

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: "28px 24px 48px",
          display: "flex",
          flexWrap: "wrap",
          gap: "32px",
        }}
      >
        <div style={{ flex: "2 1 480px", minWidth: 0 }}>
          <Suspense fallback={<div>Loading...</div>}>
            {!isSent && <ContactForm isSent={isSent} handleSent={handleSent} />}
            {isSent && <SentSuccess />}
          </Suspense>
        </div>

        <div
          style={{
            flex: "1 1 260px",
            maxWidth: "320px",
            alignSelf: "flex-start",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.brand.border}`,
            borderRadius: "10px",
            padding: "24px",
            boxSizing: "border-box",
          }}
        >
          <div style={eyebrowStyle}>{visitUsString(language)}</div>
          <div
            style={{
              fontFamily: theme.typography.secondaryFontFamily,
              fontSize: "18px",
              fontWeight: 600,
              color: theme.palette.primary.dark,
              marginBottom: "4px",
            }}
          >
            {street}
          </div>
          <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "14px", color: theme.palette.text.secondary, marginBottom: "10px" }}>
            {city}, {state} {zip}
          </div>
          <a href={ADDRESS_MAPS_URL} target="_blank" rel="noreferrer" style={primaryLinkStyle}>
            {getDirectionsString(language)} →
          </a>

          <div style={{ borderTop: `1px solid ${theme.palette.brand.border}`, margin: "20px 0" }} />

          <div style={eyebrowStyle}>{sundayWorshipString(language)}</div>
          <div
            style={{
              fontFamily: theme.typography.secondaryFontFamily,
              fontSize: "20px",
              fontWeight: 600,
              color: theme.palette.primary.dark,
            }}
          >
            {SUNDAY_SERVICES.map((s) => s.displayTime).join(" · ")}
          </div>
          <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "13px", color: theme.palette.text.secondary, marginBottom: "10px" }}>
            {morningEveningServicesString(language)}
          </div>
          <Link to={serviceTimesPath} style={primaryLinkStyle}>
            {viewFullScheduleString(language)} →
          </Link>
        </div>
      </div>

      <div style={{ position: "relative", width: "100%", height: "min(300px, 30vh)" }}>
        {!mapLoaded && <Skeleton variant="rectangular" style={{ width: "100%", height: "100%" }} />}
        <iframe
          src={`https://snazzymaps.com/embed/${snazzy()}`}
          width="100%"
          height="100%"
          onLoad={() => setMapLoaded(true)}
          style={{ border: "none", display: mapLoaded ? "block" : "none" }}
          title="Bethel Bible Church location map"
        ></iframe>
      </div>
    </div>
  );
};

export default Contact;
